import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './vendor/leaflet.contextmenu';

export type LeafletMapListener = {
  markerClicked(markerId: string): void | Promise<void>;
  contextMenuItemClicked(itemId: string, position: L.LatLng): void | Promise<void>;
};

export type ContextMenuItem = {
  id: string;
  text: string;
};

export type TileLayer = {
  id: string;
  name: string;
  urlTemplate: string;
  minZoom: number;
  maxZoom: number;
  attribution?: string;
  isDefault: boolean;
  isUsedForFullZoom: boolean;
};

export type MapLayer = {
  id: string;
  title: string;
  isDefaultVisible: boolean;
  borderColor: string;
  fillColor: string;
  geojsonUrl: string;
};

export class LeafletMap {
  private readonly listener: LeafletMapListener;
  private readonly map: L.Map;

  private readonly zoomedInLayerLevel = 19;

  private lastActiveLayer?: L.TileLayer;

  private layerControl: L.Control.Layers = L.control.layers({}, undefined, { position: 'topright' });

  private zoomedInLayer?: L.TileLayer;

  private overlayLayers: L.GeoJSON[] = [];

  private tileLayersById: Map<string, L.TileLayer> = new Map();
  private overlayLayersById: Map<string, L.GeoJSON> = new Map();
  private layerControlVisible: boolean = true;
  private pathLayers: L.Polyline[] = [];
  private markerLayers: L.Marker[] = [];
  private circleLayers: L.Circle[] = [];

  constructor(listener: LeafletMapListener, container: string | HTMLElement) {
    this.listener = listener;

    this.map = L.map(container, { contextmenu: true, attributionControl: false });

    const attributionControl = L.control.attribution().addTo(this.map);
    attributionControl.setPrefix('');

    this.map.zoomControl.setPosition('topright');
    this.layerControl.addTo(this.map);

    this.map.on('zoomend', () => {
      this.switchBetweenLastAndZoomedInTileLayer();
    });

    this.map.on('baselayerchange', (e) => {
      const zoom = this.map.getZoom();
      if (zoom !== this.zoomedInLayerLevel) {
        this.lastActiveLayer = e.layer as L.TileLayer;
      }
    });
  }

  destroy(): void {
    this.map.remove();
  }

  private switchBetweenLastAndZoomedInTileLayer(): void {
    if (this.zoomedInLayer) {
      const zoom = this.map.getZoom();
      if (zoom >= this.zoomedInLayerLevel) {
        this.showZoomedInTileLayer();
      } else {
        this.showPreviouslyUsedTileLayer();
      }
    }
  }

  private showPreviouslyUsedTileLayer() {
    if (this.zoomedInLayer && this.map.hasLayer(this.zoomedInLayer)) {
      this.map.removeLayer(this.zoomedInLayer);

      if (this.lastActiveLayer) {
        this.lastActiveLayer.addTo(this.map);
      }
    }
  }

  private showZoomedInTileLayer() {
    if (this.zoomedInLayer && !this.map.hasLayer(this.zoomedInLayer)) {
      this.map.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          this.lastActiveLayer = layer;
          this.map.removeLayer(layer);
        }
      });
      this.zoomedInLayer.addTo(this.map);
      this.bringOverlayLayersToFront();
    }
  }

  setView(center: L.LatLngExpression, zoom: number): void {
    this.map.setView(center, zoom);
  }

  setTileLayers(tileLayers: TileLayer[]) {
    this.clearTileLayers();
    this.tileLayersById.clear();

    tileLayers.forEach((tileLayer) => {
      const newLayer = L.tileLayer(tileLayer.urlTemplate, {
        minZoom: tileLayer.minZoom,
        maxZoom: tileLayer.maxZoom,
        attribution: tileLayer.attribution,
      });

      this.tileLayersById.set(tileLayer.id, newLayer);

      this.layerControl.addBaseLayer(newLayer, tileLayer.name);

      if (tileLayer.isDefault) {
        newLayer.addTo(this.map);
      }

      if (tileLayer.isUsedForFullZoom) {
        this.zoomedInLayer = newLayer;
      }
    });
  }

  async setGeoJsonLayers(layers: MapLayer[]): Promise<void> {
    this.clearGeoJsonLayers();
    this.overlayLayersById.clear();

    for (const layer of layers) {
      try {
        const response = await fetch(layer.geojsonUrl);
        if (!response.ok) {
          console.error(`Failed to fetch GeoJSON for layer ${layer.id}: ${response.statusText}`);
          continue;
        }

        const geojson = await response.json();

        const geoLayer = L.geoJSON(geojson, {
          style: {
            color: this.parseColor(layer.borderColor),
            fillColor: this.parseColor(layer.fillColor),
            fillOpacity: 0.3,
            weight: 2,
          },
        });

        this.overlayLayers.push(geoLayer);
        this.overlayLayersById.set(layer.id, geoLayer);
        this.layerControl.addOverlay(geoLayer, layer.title);

        if (layer.isDefaultVisible) {
          geoLayer.addTo(this.map);
        }
      } catch (error) {
        console.error(`Error loading GeoJSON layer ${layer.id}:`, error);
      }
    }
  }

  private clearGeoJsonLayers(): void {
    for (const layer of this.overlayLayers) {
      this.map.removeLayer(layer);
      this.layerControl.removeLayer(layer);
    }
    this.overlayLayers = [];
  }

  private parseColor(color: string): string {
    if (color && !color.startsWith('#') && /^[0-9A-Fa-f]+$/.test(color)) {
      if (color.length === 8) {
        return '#' + color.substring(0, 6);
      }
      return '#' + color;
    }
    return color;
  }

  addMarker(
    markerId: string,
    position: L.LatLngExpression,
    popupText?: string,
    markerIcon?: string,
    iconSize?: L.PointExpression,
    iconAnchor?: L.PointExpression,
    popupAnchor?: L.PointExpression,
    testID?: string,
  ): void {
    const marker = L.marker(position);

    if (markerIcon) {
      const iconSizePoint: L.PointExpression = iconSize ? iconSize : [30, 43];
      const iconAnchorPoint: L.PointExpression = iconAnchor ? iconAnchor : [15, 43];
      const popupAnchorPoint: L.PointExpression = popupAnchor ? popupAnchor : [0, -43];

      marker.setIcon(
        L.icon({
          iconUrl: markerIcon,
          iconSize: iconSizePoint,
          iconAnchor: iconAnchorPoint,
          popupAnchor: popupAnchorPoint,
        }),
      );
    }

    if (popupText) {
      marker.bindPopup(popupText);
    }

    marker.addTo(this.map).on('click', () => {
      void this.listener.markerClicked(markerId);
    });
    if (testID !== undefined) {
      marker.getElement()?.setAttribute('data-testid', testID);
    }

    this.markerLayers.push(marker);
  }

  clearMarkers(): void {
    for (const marker of this.markerLayers) {
      this.map.removeLayer(marker);
    }
    this.markerLayers = [];
  }

  addCircle(
    position: L.LatLngExpression,
    radius: number,
    color: string,
    opacity: number,
    fillColor: string,
    fillOpacity: number,
    testID?: string,
  ): void {
    const circle = L.circle(position, {
      radius: radius,
      color: color,
      opacity: opacity,
      fillColor: fillColor,
      fillOpacity: fillOpacity,
      weight: 3,
    }).addTo(this.map);
    if (testID !== undefined) {
      circle.getElement()?.setAttribute('data-testid', testID);
    }

    this.circleLayers.push(circle);
  }

  clearCircles(): void {
    this.circleLayers.forEach((circle) => {
      this.map.removeLayer(circle);
    });
    this.circleLayers = [];
  }

  addPath(path: L.LatLngExpression[], options: L.PolylineOptions): void {
    const polyline = L.polyline(path, options).addTo(this.map);
    this.pathLayers.push(polyline);
  }

  clearPaths(): void {
    for (const pathLayer of this.pathLayers) {
      this.map.removeLayer(pathLayer);
    }
    this.pathLayers = [];
  }

  clearTileLayers(): void {
    this.map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) {
        this.map.removeLayer(layer);
        this.layerControl.removeLayer(layer);
      }
    });
  }

  clearContextMenuItems(): void {
    this.map.contextmenu.removeAllItems();
  }

  addContextMenuItems(items: ContextMenuItem[]): void {
    items.forEach((item: ContextMenuItem) => {
      this.map.contextmenu.addItem({
        id: item.id,
        text: item.text,
        callback: (event) => {
          void this.listener.contextMenuItemClicked(item.id, event.latlng);
        },
      });
    });
  }

  ensurePointsVisible(
    points: L.LatLngExpression[],
    paddingTopLeft?: L.PointExpression | undefined,
    paddingBottomRight?: L.PointExpression | undefined,
  ): void {
    if (!points || points.length === 0) {
      return;
    }

    const mapBounds = this.map.getBounds();

    const allVisible = points.every((p) => mapBounds.contains(p));
    if (!allVisible) {
      const bounds = L.latLngBounds(points);

      const mapSize = this.map.getSize();
      paddingTopLeft = this.limitMapPadding(paddingTopLeft, mapSize);
      paddingBottomRight = this.limitMapPadding(paddingBottomRight, mapSize);

      this.map.fitBounds(bounds, { animate: true, paddingTopLeft, paddingBottomRight, maxZoom: 17 });
    }
  }

  limitMapPadding(padding?: L.PointExpression, mapSize?: L.Point): L.PointExpression | undefined {
    if (!padding) return undefined;

    if (!mapSize) {
      mapSize = this.map.getSize();
    }

    const point = this.toPoint(padding);

    const maxX = mapSize.x * 0.3;
    const maxY = mapSize.y * 0.3;

    return [Math.min(point.x, maxX), Math.min(point.y, maxY)];
  }

  setDisableControls(disable: boolean): void {
    const tap = (this.map as unknown as { tap?: L.Handler }).tap;

    if (disable) {
      this.map.dragging.disable();
      this.map.touchZoom.disable();
      this.map.doubleClickZoom.disable();
      this.map.scrollWheelZoom.disable();
      this.map.boxZoom.disable();
      this.map.keyboard.disable();
      this.map.removeControl(this.map.zoomControl);
      this.map.removeControl(this.layerControl);
      if (tap) {
        tap.disable();
      }
    } else {
      this.map.dragging.enable();
      this.map.touchZoom.enable();
      this.map.doubleClickZoom.enable();
      this.map.scrollWheelZoom.enable();
      this.map.boxZoom.enable();
      this.map.keyboard.enable();
      this.map.addControl(this.map.zoomControl);
      this.map.addControl(this.layerControl);
      if (tap) {
        tap.enable();
      }
    }
  }

  toPoint(point: L.PointExpression): L.Point {
    if (point instanceof L.Point) {
      return point;
    }

    return L.point(point);
  }

  hideBuiltInLayerControl(): void {
    if (this.layerControlVisible) {
      this.map.removeControl(this.layerControl);
      this.layerControlVisible = false;
    }
  }

  setActiveTileLayer(layerId: string): void {
    const targetLayer = this.tileLayersById.get(layerId);
    if (!targetLayer) {
      console.error(`Tile layer with id '${layerId}' not found`);
      return;
    }

    this.map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer && layer !== this.zoomedInLayer) {
        this.map.removeLayer(layer);
      }
    });

    targetLayer.addTo(this.map);
    this.lastActiveLayer = targetLayer;

    this.bringOverlayLayersToFront();
  }

  private bringOverlayLayersToFront(): void {
    for (const layer of this.overlayLayers) {
      if (this.map.hasLayer(layer)) {
        layer.bringToFront();
      }
    }
  }

  setOverlayVisibility(layerId: string, visible: boolean): void {
    const layer = this.overlayLayersById.get(layerId);
    if (!layer) {
      console.error(`Overlay layer with id '${layerId}' not found`);
      return;
    }

    if (visible) {
      if (!this.map.hasLayer(layer)) {
        layer.addTo(this.map);
      }
      layer.bringToFront();
    } else if (this.map.hasLayer(layer)) {
      this.map.removeLayer(layer);
    }
  }
}
